import { toast } from 'react-toastify';
import { t } from 'i18next';

// Interfaces para diferentes tipos de erro
export interface ApiError {
  message: string;
  code?: string;
  status?: number;
  details?: unknown;
}

export interface ValidationError {
  field: string;
  message: string;
  code: string;
}

export interface NetworkError {
  message: string;
  isNetworkError: boolean;
  timeout?: boolean;
}

export interface AuthError {
  message: string;
  isAuthError: boolean;
  requiresLogin?: boolean;
}

// Tipos de erro unificados
export type AppError = ApiError | ValidationError | NetworkError | AuthError;

// Enum para códigos de erro
export enum ErrorCodes {
  // Erros de API
  API_ERROR = 'API_ERROR',
  SERVER_ERROR = 'SERVER_ERROR',
  NOT_FOUND = 'NOT_FOUND',
  FORBIDDEN = 'FORBIDDEN',
  UNAUTHORIZED = 'UNAUTHORIZED',
  
  // Erros de validação
  VALIDATION_ERROR = 'VALIDATION_ERROR',
  REQUIRED_FIELD = 'REQUIRED_FIELD',
  INVALID_FORMAT = 'INVALID_FORMAT',
  
  // Erros de rede
  NETWORK_ERROR = 'NETWORK_ERROR',
  TIMEOUT_ERROR = 'TIMEOUT_ERROR',
  CONNECTION_ERROR = 'CONNECTION_ERROR',
  
  // Erros de autenticação
  AUTH_ERROR = 'AUTH_ERROR',
  TOKEN_EXPIRED = 'TOKEN_EXPIRED',
  INVALID_CREDENTIALS = 'INVALID_CREDENTIALS'
}

// Configuração de notificações
interface NotificationConfig {
  showToast?: boolean;
  toastType?: 'error' | 'warning' | 'info';
  logError?: boolean;
  customMessage?: string;
}

// Classe principal para tratamento de erros
export class ErrorHandler {
  private static instance: ErrorHandler;
  private errorLog: AppError[] = [];

  private constructor() {}

  public static getInstance(): ErrorHandler {
    if (!ErrorHandler.instance) {
      ErrorHandler.instance = new ErrorHandler();
    }
    return ErrorHandler.instance;
  }

  // Função principal para tratar erros de API
  public handleApiError(
    error: unknown, 
    context?: string,
    config: NotificationConfig = {}
  ): ApiError {
    const apiError = this.parseApiError(error);
    
    if (config.logError !== false) {
      this.logError(apiError, context);
    }

    if (config.showToast !== false) {
      this.showErrorNotification(apiError, config);
    }

    return apiError;
  }

  // Parse de erro de API
  private parseApiError(error: unknown): ApiError {
    if (this.isAxiosError(error)) {
      const status = error.response?.status;
      const message = error.response?.data?.message || error.message;
      const code = this.getErrorCodeFromStatus(status);

      return {
        message: this.getLocalizedMessage(code, message),
        code,
        status,
        details: error.response?.data
      };
    }

    if (error instanceof Error) {
      return {
        message: this.getLocalizedMessage(ErrorCodes.API_ERROR, error.message),
        code: ErrorCodes.API_ERROR,
        details: error
      };
    }

    return {
      message: this.getLocalizedMessage(ErrorCodes.API_ERROR, 'Erro desconhecido'),
      code: ErrorCodes.API_ERROR,
      details: error
    };
  }

  // Verificar se é erro do Axios
  private isAxiosError(error: unknown): error is any {
    return error && typeof error === 'object' && 'response' in error;
  }

  // Obter código de erro baseado no status HTTP
  private getErrorCodeFromStatus(status?: number): string {
    switch (status) {
      case 400:
        return ErrorCodes.VALIDATION_ERROR;
      case 401:
        return ErrorCodes.UNAUTHORIZED;
      case 403:
        return ErrorCodes.FORBIDDEN;
      case 404:
        return ErrorCodes.NOT_FOUND;
      case 500:
        return ErrorCodes.SERVER_ERROR;
      default:
        return ErrorCodes.API_ERROR;
    }
  }

  // Obter mensagem localizada
  private getLocalizedMessage(code: string, fallback: string): string {
    const messageKey = `errors.${code.toLowerCase()}`;
    const localizedMessage = t(messageKey);
    
    // Se a tradução não existir, retorna a chave, então usamos o fallback
    return localizedMessage === messageKey ? fallback : localizedMessage;
  }

  // Mostrar notificação de erro
  private showErrorNotification(error: ApiError, config: NotificationConfig): void {
    const message = config.customMessage || error.message;
    const toastType = config.toastType || 'error';

    switch (toastType) {
      case 'error':
        toast.error(message);
        break;
      case 'warning':
        toast.warn(message);
        break;
      case 'info':
        toast.info(message);
        break;
    }
  }

  // Log de erro estruturado
  private logError(error: AppError, context?: string): void {
    const logEntry = {
      ...error,
      context,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href
    };

    console.error('Error logged:', logEntry);
    this.errorLog.push(error);

    // Manter apenas os últimos 100 erros
    if (this.errorLog.length > 100) {
      this.errorLog = this.errorLog.slice(-100);
    }
  }

  // Tratar erros de validação
  public handleValidationError(
    errors: ValidationError[],
    config: NotificationConfig = {}
  ): void {
    errors.forEach(error => {
      if (config.showToast !== false) {
        toast.error(error.message);
      }
      
      if (config.logError !== false) {
        this.logError(error, 'validation');
      }
    });
  }

  // Tratar erros de rede
  public handleNetworkError(
    error: NetworkError,
    config: NotificationConfig = {}
  ): void {
    const message = config.customMessage || 
      (error.timeout ? 
        t('errors.timeout_error') : 
        t('errors.network_error')
      );

    if (config.showToast !== false) {
      toast.error(message);
    }

    if (config.logError !== false) {
      this.logError(error, 'network');
    }
  }

  // Tratar erros de autenticação
  public handleAuthError(
    error: AuthError,
    config: NotificationConfig = {}
  ): void {
    const message = config.customMessage || error.message;

    if (config.showToast !== false) {
      toast.error(message);
    }

    if (config.logError !== false) {
      this.logError(error, 'auth');
    }

    // Redirecionar para login se necessário
    if (error.requiresLogin) {
      setTimeout(() => {
        window.location.href = '/login';
      }, 2000);
    }
  }

  // Obter logs de erro
  public getErrorLogs(): AppError[] {
    return [...this.errorLog];
  }

  // Limpar logs de erro
  public clearErrorLogs(): void {
    this.errorLog = [];
  }

  // Retry automático para operações
  public async withRetry<T>(
    operation: () => Promise<T>,
    maxRetries: number = 3,
    delay: number = 1000
  ): Promise<T> {
    let lastError: unknown;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        return await operation();
      } catch (error) {
        lastError = error;
        
        if (attempt === maxRetries) {
          throw error;
        }

        // Delay exponencial
        const retryDelay = delay * Math.pow(2, attempt - 1);
        await new Promise(resolve => setTimeout(resolve, retryDelay));
      }
    }

    throw lastError;
  }
}

// Instância singleton
export const errorHandler = ErrorHandler.getInstance();

// Funções de conveniência
export const handleApiError = (
  error: unknown, 
  context?: string,
  config?: NotificationConfig
): ApiError => {
  return errorHandler.handleApiError(error, context, config);
};

export const handleValidationError = (
  errors: ValidationError[],
  config?: NotificationConfig
): void => {
  errorHandler.handleValidationError(errors, config);
};

export const handleNetworkError = (
  error: NetworkError,
  config?: NotificationConfig
): void => {
  errorHandler.handleNetworkError(error, config);
};

export const handleAuthError = (
  error: AuthError,
  config?: NotificationConfig
): void => {
  errorHandler.handleAuthError(error, config);
};

// Hook para React components
export const useErrorHandler = () => {
  return {
    handleApiError,
    handleValidationError,
    handleNetworkError,
    handleAuthError,
    withRetry: errorHandler.withRetry.bind(errorHandler),
    getErrorLogs: errorHandler.getErrorLogs.bind(errorHandler),
    clearErrorLogs: errorHandler.clearErrorLogs.bind(errorHandler)
  };
};

export default errorHandler;